import { useCallback, useMemo } from "react";
import {
  toastManager,
  type EntitySelectorConfig,
  type EntitySelection,
} from "@salestrak/ui";
import type { EntityRefValue, ObjectAccessLevel } from "#app/lib/data";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { useMentionAccessActions } from "#app/lib/mention-access-dialog-context";
import { canManageSharing } from "#app/lib/utils/access-level";
import { copyToClipboard } from "#app/lib/utils/copy-to-clipboard";
import { useIssueCardActions } from "./use-issue-card-actions";
import { useStagePool, STAGE_POOL_ID } from "#app/components/entity-selectors/hooks/use-stage-pool";
import { useWorkflowPool, WORKFLOW_POOL_ID } from "#app/components/entity-selectors/hooks/use-workflow-pool";
import { useUserPool, USER_POOL_ID } from "#app/components/entity-selectors/hooks/use-user-pool";
import { assigneeRefToEntityItem } from "#app/components/entity-selectors/utils";
import type { Omittable } from "@salestrak/ui";

export { STAGE_POOL_ID, WORKFLOW_POOL_ID, USER_POOL_ID };

/** Minimal issue shape accepted by useIssueActions — both IssueSummaryFragment and Issue satisfy this. */
export type IssueActionRef = Omittable<{
  id: string;
  issueId: string;
  name: string;
  stageId: string;
  workflowId: string;
  archived?: boolean | null;
  assignees?: EntityRefValue[];
  // Resolved per-viewer access tier — drives gating on actions like
  // "Sharing and permissions" (only viewers with `full_edit` see it).
  // Optional so callsites that don't track it stay valid.
  viewerAccessLevel?: ObjectAccessLevel | null;
  // Backend-resolved per-viewer mutation gates. `if_created` and other
  // ownership-conditional cell states collapse into these booleans so
  // the menu doesn't need to re-derive them from the flat perm map.
  viewerCanDelete?: boolean;
  viewerCanArchive?: boolean;
}>;

export function useIssueActions(issue: IssueActionRef) {
  const {
    renameIssue,
    archiveIssue,
    unarchiveIssue,
    deleteIssue,
    changeStage,
    toggleFavorite,
    isIssueFavorited,
    moveToWorkflow,
    updateAssignees,
  } = useIssueCardActions();

  const { pool: stagePool } = useStagePool(issue.workflowId);
  const excludeWorkflows = useMemo(() => [issue.workflowId], [issue.workflowId]);
  const { pool: workflowPool } = useWorkflowPool({ exclude: excludeWorkflows });
  const { pool: userPool } = useUserPool();

  const isFavorited = isIssueFavorited(issue.issueId);

  const stageConfig = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [stagePool],
      maxSelections: 1,
      searchPlaceholder: "Search stages...",
      emptyMessage: "No matching stages",
    }),
    [stagePool],
  );

  const stageValue = useMemo<EntitySelection[]>(() => {
    const item = stagePool.items.find((i) => i.id === issue.stageId);
    if (!item) return [];
    return [{ id: item.id, poolId: STAGE_POOL_ID, entity: item }];
  }, [stagePool.items, issue.stageId]);

  const workflowConfig = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [workflowPool],
      maxSelections: 1,
      searchPlaceholder: "Search workflows...",
      emptyMessage: "No other workflows",
    }),
    [workflowPool],
  );

  const assigneeConfig = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [userPool],
      searchPlaceholder: "Search members...",
      emptyMessage: "No members found",
    }),
    [userPool],
  );

  const assigneeSelections = useMemo<EntitySelection[]>(() => {
    const poolMap = new Map(userPool.items.map((item) => [item.id, item]));
    return (issue.assignees ?? [])
      .filter((a) => a.user || poolMap.has(a.entityId))
      .map((a) => {
        // Assignees missing from the pool (e.g. removed members) still resolve
        // through the shared assignee-ref mapper, not an inline avatar config.
        const entity = poolMap.get(a.entityId) ?? assigneeRefToEntityItem(a);
        return { id: a.entityId, poolId: USER_POOL_ID, entity };
      });
  }, [issue.assignees, userPool.items]);

  const handleStageValueChange = useCallback(
    (selections: EntitySelection[]) => {
      const selected = selections[0];
      if (selected && selected.id !== issue.stageId) {
        changeStage(issue, selected.id);
      }
    },
    [issue, changeStage],
  );

  // Mirrors the note composer's @-mention guard: after adding someone
  // as an assignee, the globally-mounted dialog checks whether
  // they can actually see the issue and prompts the actor to grant
  // access when they can't. Never blocks the update itself.
  const { promptIfMissing } = useMentionAccessActions();
  const viewerCanManageSharing = canManageSharing(issue.viewerAccessLevel);

  const handleAssigneeValueChange = useCallback(
    (selections: EntitySelection[]) => {
      // Diff against the current assignee set so only newly-added users
      // trigger the access prompt — removals and no-ops stay silent.
      const prevIds = new Set((issue.assignees ?? []).map((a) => a.entityId));
      const addedIds = selections
        .map((s) => s.id)
        .filter((id) => !prevIds.has(id));
      updateAssignees(issue.issueId, selections.map((s) => s.id));
      if (addedIds.length) {
        promptIfMissing({
          issueId: issue.issueId,
          userIds: addedIds,
          viewerCanManageSharing,
          variant: "assignment",
        });
      }
    },
    [
      issue.issueId,
      issue.assignees,
      updateAssignees,
      promptIfMissing,
      viewerCanManageSharing,
    ],
  );

  const handleWorkflowValueChange = useCallback(
    (selections: EntitySelection[]) => {
      const selected = selections[0];
      if (selected) {
        moveToWorkflow(issue, selected.id);
      }
    },
    [issue, moveToWorkflow],
  );

  const handleFavorite = useCallback(() => {
    toggleFavorite(issue.issueId);
  }, [issue.issueId, toggleFavorite]);

  const { activeWorkspace } = useWorkspace();
  const handleCopyLink = useCallback(async () => {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "";
    const url = `${origin}/${activeWorkspace?.urlPath ?? ""}/workflow/${issue.workflowId}?issue=${issue.issueId}`;
    const ok = await copyToClipboard(url);
    if (ok) {
      toastManager.add({ title: "Link copied", type: "success" });
    } else {
      toastManager.add({ title: "Failed to copy link", type: "error" });
    }
  }, [activeWorkspace?.urlPath, issue.workflowId, issue.issueId]);

  const handleArchive = useCallback(async () => {
    if (issue.archived) {
      await unarchiveIssue(issue);
    } else {
      await archiveIssue(issue);
    }
  }, [issue, archiveIssue, unarchiveIssue]);

  const handleDelete = useCallback(async () => {
    await deleteIssue(issue);
  }, [issue, deleteIssue]);

  return {
    isFavorited,
    isArchived: !!issue.archived,
    hasStages: stagePool.items.length > 0,
    hasOtherWorkflows: workflowPool.items.length > 0,
    stageConfig,
    stageValue,
    workflowConfig,
    assigneeConfig,
    assigneeSelections,
    handleStageValueChange,
    handleAssigneeValueChange,
    handleWorkflowValueChange,
    handleFavorite,
    handleCopyLink,
    handleArchive,
    handleDelete,
    renameIssue,
  };
}
